import { getSessionId } from '@ab-core-functions/tracking';
import { ICON_POSITION } from '@ab-core/button';
import type { ComboButtonType } from '@ab-core/button/comboButton';
import ComboButton from '@ab-core/button/comboButton';
import { ROUTES, formatPrice, isProductListOverviewPage } from '@ab-core/functions';
import type { PositionPriceObject } from '@ab-core/graphql/dist';
import { useSearchTrackingRecommendationClick } from '@ab-core/hooks-v2';
import AddLineItem from '@ab-core/hooks/cartNew/LineItems/addToCart/addToCart';
import type { IconProps } from '@ab-core/icon';
import { LINK_VARIANT, Link } from '@ab-core/link';
import useText from '@ab-core/localization/src/useText';
import { cartStore, productStore } from '@ab-core/store';
import { getTestIdProp } from '@ab-core/testing';
import Text, { FontTypes } from '@ab-core/text';
import { toast } from '@ab-core/toast';
import type { MoneyObject } from '@ab-core/types/src';
import type { FC } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAccessoryCrossSelling, setBoughtProductForAccessories } from '@ab-core/crosselling-accessories';
import { SPECIAL_SUPPLIER_IDS } from '../../mapper';

type AddToCartType = {
    sku: string;
    isUnqualifiedContractProduct: boolean;
    quantity: number;
    positionText?: string;
    chargeNr?: string;
    showSecondaryButton?: boolean;
    cartId?: string;
    mainId?: string;
    maxQuantity?: number;
    supplierId?: string;
    stockQuantity?: number;
    successorProduct?: string;
    ean?: string;
} & Pick<PositionPriceObject, 'value'>;

const PLACEHOLDER_SPLITT = '$placeholder';

export const AddToCart: FC<AddToCartType> = ({
    value,
    sku,
    isUnqualifiedContractProduct,
    quantity,
    chargeNr,
    positionText,
    showSecondaryButton,
    cartId,
    mainId,
    maxQuantity,
    supplierId,
    stockQuantity,
    successorProduct,
    ean
}) => {
    const productAddedToCartNotification = useText('cartList.productAddedToCartNotification').split(PLACEHOLDER_SPLITT);

    const { addLineItem, success, pending, response, error } = AddLineItem(cartId || '');
    const { searchTrackingRecommendationClick } = useSearchTrackingRecommendationClick();

    const showItShopButton = supplierId === SPECIAL_SUPPLIER_IDS.AVM && stockQuantity === 0;
    const showInquiryButton = isUnqualifiedContractProduct || showItShopButton;

    const { suppressAccessoryCrossSelling } = useAccessoryCrossSelling();

    const cartButtonsDisabled = cartStore.getters.useCartButtonDisabled();
    const [isPriceReady, setIsPriceReady] = useState(false);

    const lastValidValue = useRef(value);

    useEffect(() => {
        if (value) {
            if (!isPriceReady) {
                setIsPriceReady(true);
            }

            lastValidValue.current = value;
        }
    }, [value, isPriceReady]);

    useEffect(() => {
        if (success) {
            response?.upsertLineItemToCarts?.forEach((item) => {
                toast.success(
                    'Warenkorb aktualisiert',
                    <Text
                        type={FontTypes.Subtitle}
                        color="gray70"
                        {...getTestIdProp('toast-success-artikel-hinzugefügt-message')}
                    >
                        {productAddedToCartNotification[0]}
                        <Link variant={LINK_VARIANT.LINK_ONLY} to={`${ROUTES.CART}?${item.id}`}>
                            {
                                item?.custom?.customFieldsRaw?.find(
                                    (customFieldRaw) => customFieldRaw.name === 'cart-name'
                                )?.value
                            }
                        </Link>
                        {productAddedToCartNotification[1]}
                    </Text>
                );
            });

            if (lastValidValue.current && !isPriceReady) {
                setIsPriceReady(true);
            }

            if (isProductListOverviewPage(location.pathname) && !suppressAccessoryCrossSelling) {
                setBoughtProductForAccessories(sku);
            }

            cartStore.setters.setCartButtonDisabled(false);
        }
    }, [success, response, isPriceReady, sku, suppressAccessoryCrossSelling]);

    useEffect(() => {
        if (error) {
            toast.error('Fehler beim hinzufügen');

            if (lastValidValue.current && !isPriceReady) {
                setIsPriceReady(true);
            }

            cartStore.setters.setCartButtonDisabled(false);
        }
    }, [error, isPriceReady]);

    const sendRecommendationTracking = useCallback(() => {
        const sid = getSessionId();

        if (!sid || !mainId) {
            return;
        }

        searchTrackingRecommendationClick({ id: sku, mainId, sid });
    }, [mainId, sku, searchTrackingRecommendationClick]);

    const addToCart = useCallback(() => {
        if (isUnqualifiedContractProduct || !cartId) {
            return;
        }

        const fields = [];

        if (positionText) {
            fields.push({
                name: 'lineitem-positionstext',
                value: positionText
            });
        }

        if (chargeNr) {
            fields.push({
                name: 'lineitem-chargeNr',
                value: chargeNr
            });
        }

        sendRecommendationTracking();

        addLineItem({
            variables: {
                cartIds: cartId,
                lineItemInput: [
                    {
                        quantity,
                        sku,
                        fields,
                        id: undefined
                    }
                ]
            }
        });
    }, [
        isUnqualifiedContractProduct,
        cartId,
        positionText,
        chargeNr,
        sendRecommendationTracking,
        addLineItem,
        quantity,
        sku
    ]);

    const openAddToCartOrListModal = useCallback(() => {
        productStore.setters.setCartOrShoppingListModal({
            sku,
            quantity,
            isUnqualifiedContractProduct
        });
        productStore.setters.setShowCartOrShoppingListModal(true);
    }, [sku, quantity, isUnqualifiedContractProduct]);

    const triggerCart = useCallback(() => {
        if (showItShopButton) {
            productStore.setters.setProduct({ quantityUnit: '', sku, ean });
            productStore.setters.setShowItShopModal(true);
        } else if (isUnqualifiedContractProduct) {
            productStore.setters.setShowContactPersonModal(true);
        } else if (successorProduct) {
            productStore.setters.setSuccessorSku(successorProduct);
            productStore.setters.setQuantity(quantity);
            productStore.setters.setProduct({ quantityUnit: '', sku });
            productStore.setters.setShowSuccessorModal(true);
        } else {
            cartStore.setters.setCartButtonDisabled(true);
            addToCart();
        }
    }, [showItShopButton, isUnqualifiedContractProduct, successorProduct, addToCart]);

    const secondaryButtonProp = useMemo((): { secondary: ComboButtonType['secondary'] } => {
        if (!showSecondaryButton) {
            return { secondary: undefined };
        }

        return {
            secondary: {
                onClick: openAddToCartOrListModal,
                iconName: 'ChevronDown',
                ...getTestIdProp('add-to-cart-modal-button')
            }
        };
    }, [showSecondaryButton, openAddToCartOrListModal]);

    const getButtonText = () => {
        if (showItShopButton) {
            return 'zum IT-Shop';
        }

        if (isUnqualifiedContractProduct) {
            return 'Anfragen';
        }

        const displayValue = value || lastValidValue.current;

        return displayValue ? formatPrice(displayValue as MoneyObject) : '';
    };

    const primaryButtonProps = useMemo((): { primary: ComboButtonType['primary'] } => {
        let iconName: IconProps['name'] | undefined = 'Cart';

        if (showInquiryButton) {
            iconName = undefined;
        }

        if (pending) {
            iconName = 'Loader';
        }

        return {
            primary: {
                ...getTestIdProp('search-add-to-cart-button'),
                onClick: triggerCart,
                fluid: true,
                iconPosition: ICON_POSITION.LEFT,
                children: getButtonText(),
                iconName
            }
        };
    }, [value, showInquiryButton, showItShopButton, isUnqualifiedContractProduct, pending, triggerCart]);

    const isDisabled = useMemo(() => {
        if (maxQuantity === 0) {
            return true;
        }

        if (showInquiryButton) {
            return false;
        }

        const hasValidPrice = isPriceReady || !!lastValidValue.current;

        return !hasValidPrice || cartButtonsDisabled;
    }, [isPriceReady, cartButtonsDisabled, showInquiryButton, maxQuantity]);

    return (
        <div>
            <ComboButton disabled={isDisabled} {...secondaryButtonProp} {...primaryButtonProps} />
        </div>
    );
};
